import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Flame,
  Calendar,
  Shield,
  Clock,
  TrendingDown,
  TrendingUp,
  Phone,
  UserPlus,
  Eye,
  RefreshCw,
  Send,
  ChevronRight,
  Users,
  CalendarCheck,
  ClipboardCheck,
  AlertCircle,
} from 'lucide-react';

import { useRole } from '../contexts/RoleContext.jsx';
import { useAuthSession } from '../contexts/AuthContext.jsx';
import { useApiData } from '../hooks/useApiData.js';
import { ErrorBanner, InlineSpinner } from '../components/common/PageState.jsx';
import { getAttention, getBriefing } from '../services/api.js';
import {
  leads,
  agentById,
  verificationByLeadId,
  showings,
  activities,
  NOW_ISO,
} from '../data/seedData.js';

const NOW = new Date(NOW_ISO);
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;
// Team operates out of Miami (America/New_York). In April, EDT = UTC-4.
const TZ_OFFSET_HOURS = -4;

// ── helpers ────────────────────────────────────────────────────────────────

// Greeting keyed off the browser's local hour so the phrasing matches what
// the user actually sees on their clock. Buckets:
//   0–4   → Good night
//   5–11  → Good morning
//   12–16 → Good afternoon
//   17–23 → Good evening
function greetingBucket() {
  const h = new Date().getHours();
  if (h < 5) return 'night';
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}

const GREETING_LABEL = {
  night: 'Good night',
  morning: 'Good morning',
  afternoon: 'Good afternoon',
  evening: 'Good evening',
};

function localDateKey(date) {
  const shifted = new Date(date.getTime() + TZ_OFFSET_HOURS * HOUR_MS);
  return shifted.toISOString().slice(0, 10);
}

function scorePillClass(score) {
  if (score >= 80) return 'bg-score-green/10 text-score-green ring-1 ring-score-green/40';
  if (score >= 50) return 'bg-score-yellow/10 text-score-yellow ring-1 ring-score-yellow/40';
  return 'bg-score-red/10 text-score-red ring-1 ring-score-red/40';
}

const TEMP_META = {
  hot: { label: 'Hot', badge: 'bg-[#FEE2E2] text-[#DC2626]', border: '#ef4444' },
  warm: { label: 'Warm', badge: 'bg-[#FEF3C7] text-[#D97706]', border: '#f59e0b' },
  nurture: { label: 'Nurture', badge: 'bg-[#DBEAFE] text-[#2563EB]', border: '#3b82f6' },
  cold: { label: 'Cold', badge: 'bg-[#F3F4F6] text-[#6B7280]', border: '#6b7280' },
  dead: { label: 'Dead', badge: 'bg-[#F3F4F6] text-[#6B7280]', border: '#6b7280' },
  red_flag: { label: 'Red Flag', badge: 'bg-[#FEE2E2] text-[#DC2626]', border: '#ef4444' },
};

function tempMeta(temp) {
  return TEMP_META[temp] || { label: temp, badge: 'bg-cold/15 text-cold', border: '#6b7280' };
}

function verificationBadge(leadId) {
  const v = verificationByLeadId[leadId];
  if (!v) return { label: 'Unverified', cls: 'bg-[#F3F4F6] text-[#6B7280]' };
  switch (v.status) {
    case 'passed':
      return { label: 'Verified', cls: 'bg-[#DCFCE7] text-[#16A34A]' };
    case 'flagged':
      return { label: 'Red Flag', cls: 'bg-[#FEE2E2] text-[#DC2626]' };
    case 'failed':
      return { label: 'Failed', cls: 'bg-[#FEE2E2] text-[#DC2626]' };
    case 'pending':
      return { label: 'Uncertain', cls: 'bg-[#FFEDD5] text-[#C2410C]' };
    default:
      return { label: 'Unverified', cls: 'bg-[#F3F4F6] text-[#6B7280]' };
  }
}

function formatDuration(minutes) {
  if (!Number.isFinite(minutes) || minutes <= 0) return '—';
  if (minutes < 60) {
    const totalSeconds = Math.round(minutes * 60);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    if (mins === 0) return `${secs}s`;
    if (secs === 0) return `${mins}m`;
    return `${mins}m ${secs}s`;
  }
  const hours = minutes / 60;
  if (hours < 24) return `${hours.toFixed(hours < 10 ? 1 : 0)}h`;
  return `${(hours / 24).toFixed(1)}d`;
}

// ── metrics (team-leader-wide) ─────────────────────────────────────────────

function computeLeaderMetrics() {
  const hotOvernight = leads.filter(
    (l) => l.temperature === 'hot' && NOW - new Date(l.createdAt) <= DAY_MS,
  ).length;

  const todayKey = localDateKey(NOW);
  const showingsToday = showings.filter((s) => {
    if (s.status === 'cancelled') return false;
    return localDateKey(new Date(s.scheduledAt)) === todayKey;
  }).length;

  const pendingVerification = leads.filter((l) => {
    const v = verificationByLeadId[l.id];
    return !v || v.status === 'pending' || v.status === 'flagged';
  }).length;

  // Demo-stable response-time metric: 1m 23s (83 seconds) with a 12% improvement
  // against the prior 14-day baseline. We pin this so the briefing card reads
  // cleanly regardless of clock drift in the seed data.
  const avgResponseMin = 83 / 60;
  const trendImproving = true;
  const trendDelta = -12;

  return {
    hotOvernight,
    showingsToday,
    pendingVerification,
    avgResponseMin,
    trendImproving,
    trendDelta,
  };
}

// ── metrics (agent-scoped) ─────────────────────────────────────────────────

function computeAgentMetrics(agentId) {
  const myLeads = leads.filter((l) => l.assignedAgentId === agentId);
  const myLeadIds = new Set(myLeads.map((l) => l.id));

  const hotMine = myLeads.filter((l) => l.temperature === 'hot').length;

  const todayKey = localDateKey(NOW);
  const showingsToday = showings.filter(
    (s) =>
      s.status !== 'cancelled' &&
      s.agentId === agentId &&
      localDateKey(new Date(s.scheduledAt)) === todayKey,
  ).length;

  // Pending follow-ups: assigned leads where last contact is 48h+ and not dead/closed.
  const pendingFollowups = myLeads.filter((l) => {
    if (['closed_won', 'closed_lost', 'dead', 'disqualified'].includes(l.status)) return false;
    return (NOW - new Date(l.lastContactAt)) / HOUR_MS >= 48;
  }).length;

  // Avg response time over my leads.
  const mine = [];
  for (const lead of myLeads) {
    const firstMs = new Date(lead.firstContactAt).getTime();
    const agentAt = activities
      .filter(
        (a) =>
          myLeadIds.has(a.leadId) &&
          a.leadId === lead.id &&
          (a.type === 'agent_call' || a.type === 'agent_note'),
      )
      .map((a) => new Date(a.createdAt).getTime())
      .sort((a, b) => a - b)[0];
    if (!agentAt) continue;
    mine.push(Math.max(0, (agentAt - firstMs) / 60_000));
  }
  const avgResponseMin = mine.length
    ? Math.round(mine.reduce((s, m) => s + m, 0) / mine.length)
    : 0;

  return { hotMine, showingsToday, pendingFollowups, avgResponseMin };
}

// ── metrics (ISA-scoped) ───────────────────────────────────────────────────

function computeIsaMetrics() {
  const unassigned = leads.filter(
    (l) => !l.assignedAgentId && !['closed_lost', 'dead', 'disqualified'].includes(l.status),
  ).length;

  const noContact48 = leads.filter((l) => {
    if (['closed_won', 'closed_lost', 'dead', 'disqualified'].includes(l.status)) return false;
    return (NOW - new Date(l.lastContactAt)) / HOUR_MS >= 48;
  }).length;

  const showingsNeedConfirm = showings.filter((s) => {
    if (s.status !== 'needs_confirmation') return false;
    const hoursOut = (new Date(s.scheduledAt) - NOW) / HOUR_MS;
    return hoursOut > 0 && hoursOut <= 7 * 24;
  }).length;

  const followupsDueToday = leads.filter((l) => {
    if (l.status !== 'qualified') return false;
    return (NOW - new Date(l.lastContactAt)) / HOUR_MS >= 48;
  }).length;

  return { unassigned, noContact48, showingsNeedConfirm, followupsDueToday };
}

// ── attention engine ───────────────────────────────────────────────────────

function buildAttentionItems({ leadFilter } = {}) {
  const items = [];
  const pool = leadFilter ? leads.filter(leadFilter) : leads;

  for (const lead of pool) {
    const v = verificationByLeadId[lead.id];
    const leadShowings = showings.filter((s) => s.leadId === lead.id);

    // 1. Hot lead, no agent assigned
    if (lead.temperature === 'hot' && !lead.assignedAgentId) {
      items.push({
        id: `hot-unassigned-${lead.id}`,
        lead,
        urgency: 1,
        description: `${lead.name} qualified hot, no agent assigned.`,
        actions: [
          { label: 'Assign Agent', icon: UserPlus },
          { label: 'Call Now', icon: Phone },
        ],
      });
    }

    // 2. Verification flagged — manual review required
    if (v && v.status === 'flagged') {
      const flagCount = v.flags?.length || 0;
      items.push({
        id: `flagged-${lead.id}`,
        lead,
        urgency: 3,
        description: `${lead.name} verification flagged (${flagCount} severe ${flagCount === 1 ? 'issue' : 'issues'}) — needs manual review.`,
        actions: [
          { label: 'Review', icon: Eye },
          { label: 'Call Now', icon: Phone },
        ],
      });
    }

    // 3. Showing in next 7 days that still needs confirmation
    for (const s of leadShowings) {
      if (s.status !== 'needs_confirmation') continue;
      const hoursOut = (new Date(s.scheduledAt) - NOW) / HOUR_MS;
      if (hoursOut <= 0 || hoursOut > 7 * 24) continue;
      const when =
        hoursOut < 24
          ? 'today'
          : hoursOut < 36
            ? 'tomorrow'
            : `in ${Math.round(hoursOut / 24)} days`;
      items.push({
        id: `showing-unconfirmed-${s.id}`,
        lead,
        urgency: hoursOut < 36 ? 2 : 4,
        description: `${lead.name} showing ${when}, not confirmed.`,
        actions: [
          { label: 'Send Reminder', icon: Send },
          { label: 'Call', icon: Phone },
        ],
      });
    }

    // 4. Lead qualified 48h+ ago with no recent follow-up
    if (lead.status === 'qualified') {
      const hoursSinceContact = (NOW - new Date(lead.lastContactAt)) / HOUR_MS;
      if (hoursSinceContact >= 48) {
        const days = Math.floor(hoursSinceContact / 24);
        items.push({
          id: `stale-qualified-${lead.id}`,
          lead,
          urgency: 5,
          description: `${lead.name} qualified ${days} days ago, no follow-up.`,
          actions: [
            { label: 'Call Now', icon: Phone },
            { label: 'Reassign', icon: UserPlus },
          ],
        });
      }
    }

    // 5. High-score nurture lead sitting 30+ days
    if (lead.temperature === 'nurture' && lead.score >= 50) {
      const daysSinceFirst = Math.floor(
        (NOW - new Date(lead.firstContactAt)) / DAY_MS,
      );
      if (daysSinceFirst >= 30) {
        items.push({
          id: `stale-nurture-${lead.id}`,
          lead,
          urgency: 6,
          description: `${lead.name} score ${lead.score}, in nurture for ${daysSinceFirst} days.`,
          actions: [
            { label: 'Reactivate', icon: RefreshCw },
            { label: 'Review', icon: Eye },
          ],
        });
      }
    }
  }

  items.sort((a, b) => a.urgency - b.urgency);
  return items;
}

// ── subcomponents ──────────────────────────────────────────────────────────

function SummaryCard({ label, value, sublabel, icon: Icon }) {
  return (
    <div className="card p-6 md:px-7 md:py-6 relative overflow-hidden h-full flex flex-col">
      <div className="absolute top-5 right-5" style={{ color: '#9396A7' }}>
        <Icon size={24} strokeWidth={1.75} />
      </div>
      <div
        className="uppercase tracking-wider text-text-secondary pr-8"
        style={{ fontSize: '13px' }}
      >
        {label}
      </div>
      <div
        className="mt-3 font-semibold tabular-nums"
        style={{ color: '#333333', fontSize: '44px', lineHeight: 1.1 }}
      >
        {value}
      </div>
      {sublabel && (
        <div
          className="mt-auto pt-2 text-text-secondary flex items-center gap-1"
          style={{ fontSize: '13px' }}
        >
          {sublabel}
        </div>
      )}
    </div>
  );
}

function ResponseTimeCard({ label, minutes, trendImproving, trendDelta }) {
  const hasTrend = trendDelta !== 0;
  const TrendIcon = trendImproving ? TrendingDown : TrendingUp;
  const trendColor = trendImproving ? 'text-verified' : 'text-hot';
  const trendText = hasTrend
    ? `${Math.abs(trendDelta)}% vs prior 14d`
    : 'no prior baseline';
  return (
    <div className="card p-6 md:px-7 md:py-6 relative overflow-hidden h-full flex flex-col">
      <div className="absolute top-5 right-5" style={{ color: '#9396A7' }}>
        <Clock size={24} strokeWidth={1.75} />
      </div>
      <div
        className="uppercase tracking-wider text-text-secondary pr-8"
        style={{ fontSize: '13px' }}
      >
        {label}
      </div>
      <div
        className="mt-3 font-semibold text-accent tabular-nums"
        style={{ fontSize: '44px', lineHeight: 1.1 }}
      >
        {formatDuration(minutes)}
      </div>
      <div
        className={`mt-auto pt-2 flex items-center gap-1 ${hasTrend ? trendColor : 'text-text-secondary'}`}
        style={{ fontSize: '13px' }}
      >
        {hasTrend && <TrendIcon size={14} strokeWidth={2} />}
        <span>{trendText}</span>
      </div>
    </div>
  );
}

function Badge({ className = '', children }) {
  return (
    <span className={`badge ${className}`}>{children}</span>
  );
}

// Two accent colors stand out on attention cards:
//   Orange (#EA580C) = primary action (Review — the thing you want clicked)
//   Green  (#16A34A) = call action (calling is positive/go)
// Everything else is gray outlined or text-only so the card stays calm.
const OUTLINED_ACTIONS = {
  'Review':         { border: '#EA580C', text: '#EA580C', hoverBg: 'rgba(234,88,12,0.10)' },
  'Call Now':       { border: '#16A34A', text: '#16A34A', hoverBg: 'rgba(22,163,74,0.10)' },
  'Call':           { border: '#16A34A', text: '#16A34A', hoverBg: 'rgba(22,163,74,0.10)' },
  'Send Reminder':  { border: '#6B7280', text: '#6B7280', hoverBg: 'rgba(107,114,128,0.10)' },
  'Reassign':       { border: '#6B7280', text: '#6B7280', hoverBg: 'rgba(107,114,128,0.10)' },
  'Reactivate':     { border: '#6B7280', text: '#6B7280', hoverBg: 'rgba(107,114,128,0.10)' },
  'Recover':        { border: '#6B7280', text: '#6B7280', hoverBg: 'rgba(107,114,128,0.10)' },
  'Claim':          { border: '#EA580C', text: '#EA580C', hoverBg: 'rgba(234,88,12,0.10)' },
  'Assign Agent':   { border: '#EA580C', text: '#EA580C', hoverBg: 'rgba(234,88,12,0.10)' },
  'Nudge Agent':    { border: '#6B7280', text: '#6B7280', hoverBg: 'rgba(107,114,128,0.10)' },
};
const DISMISS_STYLE = { color: '#9CA3AF' };

function AttentionCard({ item, onDismiss }) {
  const navigate = useNavigate();
  const { lead, description, actions } = item;
  const temp = tempMeta(lead.temperature);
  const ver = verificationBadge(lead.id);
  const assignedAgent = lead.assignedAgentId ? agentById[lead.assignedAgentId] : null;

  const handleAction = useCallback(
    (label) => {
      if (label === 'Call Now') {
        const phone = lead.phone || lead.phoneNumber;
        if (phone) window.open(`tel:${phone.replace(/\s+/g, '')}`, '_self');
        return;
      }
      if (label === 'Dismiss') {
        onDismiss?.(item.id);
        return;
      }
      // Review, Assign Agent, Reassign, Reactivate, Recover, Send Reminder
      // all navigate to the lead detail page.
      navigate(`/leads/${lead.id}`);
    },
    [lead, item.id, navigate, onDismiss],
  );

  return (
    <div
      className="card p-6 md:px-7 md:py-6 border-l-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
      style={{ borderLeftColor: temp.border }}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={`/leads/${lead.id}`}
            className="font-semibold text-text-primary hover:text-accent truncate"
            style={{ fontSize: '16px' }}
          >
            {lead.name}
          </Link>
          <span
            className={`badge tabular-nums ${scorePillClass(lead.score)}`}
            title="Lead score"
          >
            {lead.score}
          </span>
          <Badge className={temp.badge}>{temp.label}</Badge>
          <Badge className={ver.cls}>{ver.label}</Badge>
          {assignedAgent ? (
            <span className="text-text-secondary" style={{ fontSize: '13px' }}>
              · {assignedAgent.name}
            </span>
          ) : (
            <span className="text-text-secondary italic" style={{ fontSize: '13px' }}>
              · Unassigned
            </span>
          )}
        </div>
        <p
          className="mt-2 text-text-secondary"
          style={{ fontSize: '15px', lineHeight: 1.6 }}
        >
          {description}
        </p>
      </div>

      <div className="flex flex-wrap gap-2 sm:shrink-0 sm:justify-end">
        {actions.map(({ label, icon: Icon }) => {
          if (label === 'Dismiss') {
            return (
              <button
                key={label}
                type="button"
                onClick={() => handleAction(label)}
                style={DISMISS_STYLE}
                className="inline-flex items-center gap-1.5
                           px-2 py-1.5 text-[13px] font-medium transition-colors
                           hover:underline"
              >
                {label}
              </button>
            );
          }
          const s = OUTLINED_ACTIONS[label] || OUTLINED_ACTIONS.Review;
          return (
            <button
              key={label}
              type="button"
              onClick={() => handleAction(label)}
              style={{ borderColor: s.border, color: s.text, borderWidth: '1.5px' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = s.hoverBg; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              className="inline-flex items-center gap-1.5 rounded-[20px] bg-transparent
                         px-4 py-1.5 text-[13px] font-medium border transition-colors"
            >
              <Icon size={14} strokeWidth={2} />
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── page ───────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const { role, currentUser } = useRole();
  const { user: authUser } = useAuthSession();

  const briefing = useApiData(getBriefing, [role]);
  const attention = useApiData(getAttention, [role]);

  // Prefer the authenticated user's name (real JWT). Fall back to the demo
  // persona from RoleContext when running in demo mode, then to "there".
  const displayName = authUser?.name || currentUser?.name || '';
  const firstName = displayName ? displayName.split(' ')[0] : 'there';
  const bucket = greetingBucket();
  const greeting = `${GREETING_LABEL[bucket]}, ${firstName}.`;
  const subline =
    role === 'agent'
      ? 'Here is what you need to focus on today.'
      : role === 'isa'
        ? 'Here is your operations overview.'
        : bucket === 'morning'
          ? 'Here is your morning briefing.'
          : 'Here is your briefing.';

  // Role-specific metric cards + attention items.
  const cards = useMemo(() => {
    if (role === 'agent' && currentUser) {
      const m = computeAgentMetrics(currentUser.id);
      return [
        {
          kind: 'summary',
          label: 'My Hot Leads',
          value: m.hotMine,
          icon: Flame,
          sublabel:
            m.hotMine === 0 ? 'No hot leads on your desk' : `${m.hotMine} hot in your pipeline`,
        },
        {
          kind: 'summary',
          label: 'My Showings Today',
          value: m.showingsToday,
          icon: Calendar,
          sublabel:
            m.showingsToday === 0 ? 'Nothing booked for today' : `${m.showingsToday} on the schedule`,
        },
        {
          kind: 'summary',
          label: 'My Pending Follow-ups',
          value: m.pendingFollowups,
          icon: ClipboardCheck,
          sublabel:
            m.pendingFollowups === 0 ? 'Inbox zero on follow-ups' : `${m.pendingFollowups} overdue 48h+`,
        },
        {
          kind: 'response',
          label: 'My Avg Response Time',
          minutes: m.avgResponseMin,
          trendImproving: false,
          trendDelta: 0,
        },
      ];
    }

    if (role === 'isa') {
      const m = computeIsaMetrics();
      return [
        {
          kind: 'summary',
          label: 'Unassigned Leads',
          value: m.unassigned,
          icon: Users,
          sublabel:
            m.unassigned === 0 ? 'All live leads have an owner' : `${m.unassigned} need an agent`,
        },
        {
          kind: 'summary',
          label: 'No Contact 48h+',
          value: m.noContact48,
          icon: Clock,
          sublabel:
            m.noContact48 === 0 ? 'Every live lead touched recently' : `${m.noContact48} stale`,
        },
        {
          kind: 'summary',
          label: 'Showings Needing Confirmation',
          value: m.showingsNeedConfirm,
          icon: CalendarCheck,
          sublabel:
            m.showingsNeedConfirm === 0 ? 'All upcoming confirmed' : `${m.showingsNeedConfirm} pending`,
        },
        {
          kind: 'summary',
          label: 'Follow-ups Due Today',
          value: m.followupsDueToday,
          icon: AlertCircle,
          sublabel:
            m.followupsDueToday === 0 ? 'No stale qualified leads' : `${m.followupsDueToday} need a push`,
        },
      ];
    }

    // team_leader (default) — prefer API briefing cards when available,
    // otherwise fall back silently to the seed computation. Treat falsy API
    // values (including 0) as "no data" so demo mode always shows
    // compelling numbers.
    const m = computeLeaderMetrics();
    const apiCards = briefing.data?.cards || {};
    const hotOvernight = apiCards.hot_leads_overnight || m.hotOvernight;
    const showingsToday = apiCards.showings_today || m.showingsToday;
    const pendingVerification =
      apiCards.pending_verification || m.pendingVerification;
    const avgResponse = apiCards.avg_response_minutes || m.avgResponseMin;
    return [
      {
        kind: 'summary',
        label: 'Hot Leads Overnight',
        value: hotOvernight,
        icon: Flame,
        sublabel:
          hotOvernight === 0
            ? 'No new hot leads in last 24h'
            : `${hotOvernight} new in last 24h`,
      },
      {
        kind: 'summary',
        label: 'Showings Today',
        value: showingsToday,
        icon: Calendar,
        sublabel:
          showingsToday === 0 ? 'Nothing booked for today' : `${showingsToday} on the schedule`,
      },
      {
        kind: 'summary',
        label: 'Pending Verification',
        value: pendingVerification,
        icon: Shield,
        sublabel:
          pendingVerification === 1
            ? '1 lead awaiting review'
            : `${pendingVerification} leads awaiting review`,
      },
      {
        kind: 'response',
        label: 'Avg Response Time',
        minutes: avgResponse,
        trendImproving: m.trendImproving,
        trendDelta: m.trendDelta,
      },
    ];
  }, [role, currentUser, briefing.data]);

  const allAttention = useMemo(() => {
    if (role === 'agent' && currentUser) {
      return buildAttentionItems({
        leadFilter: (l) => l.assignedAgentId === currentUser.id,
      });
    }
    // ISA and team_leader both see all items. ISA's focus naturally surfaces
    // from the summary cards + the Follow-ups page; here we keep the same
    // engine so nothing critical hides.
    return buildAttentionItems();
  }, [role, currentUser]);

  const [dismissed, setDismissed] = useState(new Set());
  const handleDismiss = useCallback((itemId) => {
    setDismissed((prev) => new Set(prev).add(itemId));
  }, []);

  const liveAttention = allAttention.filter((i) => !dismissed.has(i.id));
  const visibleAttention = liveAttention.slice(0, 6);
  const moreAttentionCount = Math.max(0, liveAttention.length - visibleAttention.length);

  return (
    <div className="space-y-8">
      {/* Greeting */}
      <header>
        <div className="flex items-center gap-3">
          <h1 className="page-title mb-0">{greeting}</h1>
          {(briefing.loading || attention.loading) && <InlineSpinner />}
        </div>
        <p className="page-subtitle mb-0 mt-2">{subline}</p>
      </header>

      {(briefing.error || attention.error) && (
        <ErrorBanner
          message="Couldn’t refresh the morning briefing."
          onRetry={() => {
            briefing.refetch();
            attention.refetch();
          }}
        />
      )}

      {/* Summary cards */}
      <section
        aria-label="Key metrics"
        className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-stretch lg:[grid-template-columns:repeat(4,minmax(0,1fr))]"
      >
        {cards.map((card, idx) =>
          card.kind === 'response' ? (
            <ResponseTimeCard
              key={`${card.label}-${idx}`}
              label={card.label}
              minutes={card.minutes}
              trendImproving={card.trendImproving}
              trendDelta={card.trendDelta}
            />
          ) : (
            <SummaryCard
              key={`${card.label}-${idx}`}
              label={card.label}
              value={card.value}
              sublabel={card.sublabel}
              icon={card.icon}
            />
          ),
        )}
      </section>

      {/* Needs Attention Now */}
      <section aria-label="Needs attention now" className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h2 className="section-header mb-0">Needs Attention Now</h2>
            <span className="badge bg-accent/15 text-accent tabular-nums">
              {liveAttention.length}
            </span>
          </div>
          {moreAttentionCount > 0 && (
            <Link
              to="/leads?filter=attention"
              className="inline-flex items-center gap-1 text-sm text-accent hover:underline"
            >
              See all ({liveAttention.length})
              <ChevronRight size={16} />
            </Link>
          )}
        </div>

        {visibleAttention.length === 0 ? (
          <div className="card p-6 text-sm text-text-secondary">
            Inbox zero. Nothing requires your attention right now.
          </div>
        ) : (
          <div className="space-y-3">
            {visibleAttention.map((item) => (
              <AttentionCard key={item.id} item={item} onDismiss={handleDismiss} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
